import { FC, memo, SyntheticEvent, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Grid, Typography } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useBreakpoints } from 'hooks';
import { isEmpty } from 'lodash';

import { ReactComponent as ChevronDownIcon } from 'assets/icons/ChevronDown.svg';

import { DatePickerControl } from 'components/_basic/Form/DatePicker';
import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { RemoveButton } from 'components/_basic/Form/FieldArray/components/RemoveButton';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { Responsive } from 'components/_basic/Responsive';
import { BorderedCard } from 'components/BorderedCard';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';

import { concatWithDot } from 'helpers/string';

import { StyledAccordion, StyledAccordionDetails, StyledAccordionSummary } from './components';
import { EducationForm } from './EducationForm';
import { WorkExperience } from './WorkExperience';
import { institutionValues, PERSONNEL_PROFILES_NAMES } from '../utils/form';

import styles from './PersonnelProfiles.module.scss';

interface IProps {
  name: string;
  index?: number;
  disabled?: boolean;
  accordionExpanded?: string | false;
  onRemove?: VoidFunction;
  onAccordionChange?: (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => void;
}

export const PersonnelProfiles: FC<IProps> = memo(
  ({ name, disabled = false, index, onRemove, accordionExpanded, onAccordionChange }) => {
    const { t } = useTranslation();
    const { control } = useFormContext();
    const { isMobile } = useBreakpoints();

    const nameAndSurname = useWatch({
      name: [concatWithDot(name, PERSONNEL_PROFILES_NAMES.NAME), concatWithDot(name, PERSONNEL_PROFILES_NAMES.SURNAME)],
    })
      .join(' ')
      .trim();

    const displayName = isEmpty(nameAndSurname) ? 'Personnel' : nameAndSurname;

    const personnelProfilesContent = useMemo(
      () => (
        <Grid container spacing={3}>
          <Grid item xs={12} md="auto">
            <TooltipFormImageUpload
              {...(isMobile ? { placeholder: 'photo' } : {})}
              width={isMobile ? 74 : 136}
              height={isMobile ? 74 : 136}
              name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.FILE)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInputControl
                  control={control}
                  id={concatWithDot(name, PERSONNEL_PROFILES_NAMES.NAME)}
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.NAME)}
                  label={t('management.team.name')}
                  disabled={disabled}
                  maxLength={150}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInputControl
                  control={control}
                  id={concatWithDot(name, PERSONNEL_PROFILES_NAMES.SURNAME)}
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.SURNAME)}
                  label={t('management.team.surname')}
                  disabled={disabled}
                  maxLength={150}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePickerControl
                  control={control}
                  id={concatWithDot(name, PERSONNEL_PROFILES_NAMES.DATE_OF_BIRTH)}
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.DATE_OF_BIRTH)}
                  label={t('management.team.dateOfBirth')}
                  disabled={disabled}
                  maxDate={dayjs()}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInputControl
                  control={control}
                  id={concatWithDot(name, PERSONNEL_PROFILES_NAMES.POSITION)}
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.POSITION)}
                  label={t('management.team.currentPosition')}
                  disabled={disabled}
                  maxLength={150}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Responsive mobile>
              <FormSectionCard
                title={t('management.team.education')}
                className={styles.educationSectionMobile}
                titleWrapperClassName={styles.sectionCardTitle}
              >
                <FieldArrayComponent
                  fieldArrayStyles={{ removeButton: styles.removeButton }}
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.LIST_OF_INSTITUTIONS)}
                  initialItem={institutionValues}
                  buttonText={t('management.team.educationButtonText')}
                  disabled={disabled}
                >
                  {(namePrefix, _, remove) => (
                    <FormSectionFilledCard>
                      <EducationForm name={namePrefix} disabled={disabled} onRemove={remove} />
                    </FormSectionFilledCard>
                  )}
                </FieldArrayComponent>
              </FormSectionCard>
            </Responsive>
            <Responsive aboveMobile>
              <FormSectionFilledCard title={t('management.team.education')}>
                <FieldArrayComponent
                  fieldArrayStyles={{
                    removeButton: styles.removeButton,
                    rowContainer: styles.rowContainer,
                    addButton: styles.addButton,
                  }}
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.LIST_OF_INSTITUTIONS)}
                  initialItem={institutionValues}
                  buttonText={t('management.team.educationButtonText')}
                  disabled={disabled}
                >
                  {(namePrefix) => <EducationForm name={namePrefix} disabled={disabled} />}
                </FieldArrayComponent>
              </FormSectionFilledCard>
            </Responsive>
          </Grid>
          <Grid item xs={12}>
            <Responsive mobile>
              <FormSectionCard
                title={t('management.team.workExperience')}
                className={styles.educationSectionMobile}
                titleWrapperClassName={styles.sectionCardTitle}
              >
                <FieldArrayComponent
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.WORK_EXPERIENCE)}
                  initialItem={institutionValues}
                  buttonText={t('management.team.workExperienceButtonText')}
                  disabled={disabled}
                >
                  {(namePrefix, _, remove) => (
                    <FormSectionFilledCard>
                      <WorkExperience name={namePrefix} disabled={disabled} onRemove={remove} />
                    </FormSectionFilledCard>
                  )}
                </FieldArrayComponent>
              </FormSectionCard>
            </Responsive>
            <Responsive aboveMobile>
              <FormSectionFilledCard title={t('management.team.workExperience')}>
                <FieldArrayComponent
                  name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.WORK_EXPERIENCE)}
                  initialItem={institutionValues}
                  buttonText={t('management.team.workExperienceButtonText')}
                  disabled={disabled}
                  fieldArrayStyles={{ addButton: styles.addButton }}
                >
                  {(namePrefix) => (
                    <BorderedCard borderCardStyles={{ container: styles.borderContainer }}>
                      <WorkExperience name={namePrefix} disabled={disabled} />
                    </BorderedCard>
                  )}
                </FieldArrayComponent>
              </FormSectionFilledCard>
            </Responsive>
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(name, PERSONNEL_PROFILES_NAMES.KEY_SKILLS)}
              name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.KEY_SKILLS)}
              minRows={4}
              label={t('management.team.keySkills')}
              disabled={disabled}
              maxLength={500}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(name, PERSONNEL_PROFILES_NAMES.ACHIEVEMENTS)}
              name={concatWithDot(name, PERSONNEL_PROFILES_NAMES.ACHIEVEMENTS)}
              minRows={4}
              label={t('management.team.specialAchievements')}
              disabled={disabled}
              maxLength={500}
            />
          </Grid>
        </Grid>
      ),
      [],
    );

    return (
      <>
        <Responsive mobile>
          <StyledAccordion
            expanded={accordionExpanded === `panel${index}`}
            onChange={onAccordionChange?.(`panel${index}`)}
          >
            <StyledAccordionSummary>
              <Box className={styles.accordionHeader}>
                <Box className={styles.titleWrapper}>
                  <Box
                    className={clsx(styles.accordionHeaderIconWrapper, {
                      [styles.disabled]: disabled,
                    })}
                  >
                    <ChevronDownIcon
                      className={clsx(styles.accordionHeaderIcon, {
                        [styles.expandedIcon]: accordionExpanded === `panel${index}`,
                        [styles.disabled]: disabled,
                      })}
                    />
                  </Box>
                  <Typography
                    className={clsx(styles.title, {
                      [styles.disabled]: disabled,
                    })}
                    variant="h6"
                  >
                    {displayName}
                  </Typography>
                </Box>
                <RemoveButton
                  containerStyles={styles.removeButtonAccordion}
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove?.();
                  }}
                  canRemove
                />
              </Box>
            </StyledAccordionSummary>
            <StyledAccordionDetails>{personnelProfilesContent}</StyledAccordionDetails>
          </StyledAccordion>
        </Responsive>
        <Responsive aboveMobile>
          <BorderedCard>{personnelProfilesContent}</BorderedCard>
        </Responsive>
      </>
    );
  },
);
